// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import LazyImage, { LazyImagePresentationProps } from "@insite/mobius/LazyImage";
import Typography from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import styled, { css } from "styled-components";

export enum BadgeTypeValues {
    Image = "Image",
    Text = "Text",
}

const borderWidth = "1px";
const edgeSize = "8px";
export interface BadgePresentationProps {
    textBadgeCss?: InjectableCss;
    imageBadgeProps?: JSX.IntrinsicElements["img"];
}

const TextBadge = styled.div<{ backgroundColor: string; color: string; badgeTextRounding: number }>`
    text-align: center;
    > ${TypographyStyle} {
        font-size: 10px;
        line-height: 16px;
        font-weight: bold;
        display: flex;
        place-content: center;
    }
    padding: 5px 9px;
    color: #${props => props.color};
    border-radius: ${props => props.badgeTextRounding}px;
    ${({ badgeTextRounding, backgroundColor }: any) => css`
        ${badgeTextRounding === undefined || badgeTextRounding === 0
            ? css`
                  isolation: isolate;
                  margin-top: 8px;
                  display: inline-block;
                  text-align: center;
                  position: relative;
                  -webkit-clip-path: polygon(
                      ${edgeSize} 0%,
                      100% 0,
                      100% calc(100% - ${edgeSize}),
                      calc(100% - ${edgeSize}) 100%,
                      0 100%,
                      0% ${edgeSize}
                  );
                  clip-path: polygon(
                      ${edgeSize} 0%,
                      100% 0,
                      100% calc(100% - ${edgeSize}),
                      calc(100% - ${edgeSize}) 100%,
                      0 100%,
                      0% ${edgeSize}
                  );

                  &::before {
                      content: "";
                      position: absolute;
                      inset: 0;
                      background: ${getColor("primary")};
                      z-index: -2;
                  }

                  &::after {
                      content: "";
                      position: absolute;
                      inset: 0;
                      background-color: #${backgroundColor};
                      z-index: -1;
                      -webkit-clip-path: polygon(
                          ${borderWidth} calc(${edgeSize} + ${borderWidth} * 0.5),
                          calc(${edgeSize} + ${borderWidth} * 0.5) ${borderWidth},
                          calc(100% - ${borderWidth}) ${borderWidth},
                          calc(100% - ${borderWidth}) calc(100% - calc(${edgeSize} + ${borderWidth} * 0.5)),
                          calc(100% - calc(${edgeSize} + ${borderWidth} * 0.5)) calc(100% - ${borderWidth}),
                          calc(${borderWidth}) calc(100% - ${borderWidth})
                      );
                      clip-path: polygon(
                          ${borderWidth} calc(${edgeSize} + ${borderWidth} * 0.5),
                          calc(${edgeSize} + ${borderWidth} * 0.5) ${borderWidth},
                          calc(100% - ${borderWidth}) ${borderWidth},
                          calc(100% - ${borderWidth}) calc(100% - calc(${edgeSize} + ${borderWidth} * 0.5)),
                          calc(100% - calc(${edgeSize} + ${borderWidth} * 0.5)) calc(100% - ${borderWidth}),
                          calc(${borderWidth}) calc(100% - ${borderWidth})
                      );
                  }
              `
            : css`
                  border: 1px solid ${getColor("primary")};
                  background-color: #${backgroundColor};
              `}
    `}
`;

export type BadgeProps = {
    /** When type is Image an img tag will be rendered with the src being the value prop.
     * When the type is text a textbox will be rendered with the given value used as text and color scheme
     * denoted in badgeTextColor and badgeBackgroundColor
     * When no type is given or no corresponding value is given, nothing will render.
     */
    type: BadgeTypeValues;
    /** URL image path or badge text */
    value?: string;
    /** Alt text for the image badge */
    imageAltText?: string;
    /** Hex code for badge text color
     * Default is white (FFFFFF)
     */
    badgeTextColor?: string;
    /** Hex code for badge background color
     * Default is black (000000)
     */
    badgeBackgroundColor?: string;
    /** Number used to determine border radius for text badge in pixels
     * Default is 0
     */
    badgeTextRounding?: number;
} & BadgePresentationProps;

const Badge = ({
    type,
    value,
    imageAltText,
    badgeTextColor,
    badgeBackgroundColor,
    badgeTextRounding,
    imageBadgeProps,
    textBadgeCss,
}: BadgeProps) => {
    if (!type || !value) {
        return null;
    }

    if (type === BadgeTypeValues.Image) {
        return <LazyImage imgProps={imageBadgeProps} src={value} altText={imageAltText} />;
    }

    if (type === BadgeTypeValues.Text) {
        return (
            <TextBadge
                backgroundColor={badgeBackgroundColor || "000000"}
                color={badgeTextColor || "FFFFFF"}
                badgeTextRounding={badgeTextRounding || 0}
            >
                <Typography {...textBadgeCss}>{value}</Typography>
            </TextBadge>
        );
    }

    return <>Unknown badge type</>;
};

/** @component */
export default Badge;
